import { useState, useEffect } from 'react'
import { Mail, LogIn, Wand2, CheckCircle, ArrowRight, Zap, Globe, Brain } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { OtpInput } from '@/components/ui'
import { AppPromoRow } from '@/components/AppPromo'
import { BrandLogo } from '@/components/BrandLogo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { T, S } from '@/lib/design'

type AuthMethod = 'otp' | 'magic_link'

function FeatureItem({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ width: 16, height: 16, borderRadius: '50%', background: T.gold, flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  )
}

function SomicastMark() {
  return <BrandLogo size={34} />
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [method, setMethod] = useState<AuthMethod>('otp')
  const [loading, setLoading] = useState(false)
  const [gLoading, setGLoading] = useState(false)
  const [otpStep, setOtpStep] = useState(false)
  const [otp, setOtp] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [magicSent, setMagicSent] = useState(false)

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

  useEffect(() => {
    if (!window.location.hash) return
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) window.history.replaceState(null, '', window.location.pathname)
    })
  }, [])

  useEffect(() => {
    if (otp.length === 6) verifyOtp()
  }, [otp])

  const sendOtp = async () => {
    if (!isValidEmail) { toast.error('Enter a valid email'); return }
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.toLowerCase().trim(),
      options: { shouldCreateUser: true },
    })
    setLoading(false)
    if (error) { toast.error(error.message); return }
    setOtpStep(true)
    toast.success('Code sent. Check your inbox')
  }

  const verifyOtp = async () => {
    if (otp.length < 6 || verifying) return
    setVerifying(true)
    const { error } = await supabase.auth.verifyOtp({
      email: email.toLowerCase().trim(),
      token: otp,
      type: 'email',
    })
    setVerifying(false)
    if (error) { toast.error(error.message); setOtp('') }
  }

  const sendMagic = async () => {
    if (!isValidEmail) { toast.error('Enter a valid email'); return }
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.toLowerCase().trim(),
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/login`,
      },
    })
    setLoading(false)
    if (error) { toast.error(error.message); return }
    setMagicSent(true)
  }

  const signInGoogle = async () => {
    setGLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        queryParams: { prompt: 'select_account' },
      },
    })
    if (error) { toast.error(error.message); setGLoading(false) }
  }

  const reset = () => { setOtpStep(false); setMagicSent(false); setOtp('') }

  return (
    <div className="login-shell">
      <div style={{
        position: 'absolute', top: -180, left: '50%', transform: 'translateX(-50%)',
        width: 600, height: 400, pointerEvents: 'none',
        background: `radial-gradient(ellipse at center, ${T.gold}12 0%, transparent 70%)`,
      }} />

      {/* Left Panel - Brand & Value Proposition */}
      <div className="login-panel-left">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 52 }}>
          <SomicastMark />
          <div>
            <div style={{ fontFamily: T.sans, fontSize: 15, fontWeight: 800, letterSpacing: 0, color: 'var(--text)' }}>
              Somicast
            </div>
            <div style={{ fontFamily: T.sans, fontSize: 12, color: 'var(--muted)', letterSpacing: 0 }}>
              Content Engine
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <ThemeToggle compact />
          </div>
        </div>

        <div style={{ marginBottom: 44 }}>
          <h1 style={{ fontFamily: T.display, fontSize: 32, fontWeight: 800, color: 'var(--text)', lineHeight: 1.2, marginBottom: 16 }}>
            Your website becomes<br />
            <span style={{ color: T.gold }}>your marketing team.</span>
          </h1>
          <p style={{ fontSize: 14, color: 'var(--dim)', lineHeight: 1.7 }}>
            No prompts. No manual scheduling.<br />
            Continuous, platform native marketing from your existing content.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
          <FeatureItem>Brand intelligence that learns your voice automatically</FeatureItem>
          <FeatureItem>Continuous distribution across platform campaigns, always running</FeatureItem>
          <FeatureItem>Platform native output optimized for each channel</FeatureItem>
          <FeatureItem>Approval workflow with review or automation</FeatureItem>
        </div>

        <div style={{ marginTop: 24, marginBottom: 8 }}>
          <AppPromoRow />
        </div>

        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, lineHeight: 1.6 }}>
          By continuing you agree to our{' '}
          <a href="/terms" style={{ color: T.info }}>Terms</a>{' & '}
          <a href="/privacy" style={{ color: T.info }}>Privacy Policy</a>
        </p>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="login-panel-right">
        <div className="login-form">
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: T.display, fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
              Sign in
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              New? An account is created automatically.
            </p>
          </div>

          <button
            onClick={signInGoogle}
            disabled={gLoading}
            onMouseEnter={(e) => { if (!gLoading) e.currentTarget.style.borderColor = T.pepper }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)' }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 9, padding: '11px 16px', marginBottom: 20,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
              fontSize: 14, fontWeight: 650, color: 'var(--dim)',
              cursor: gLoading ? 'not-allowed' : 'pointer',
              transition: 'border-color 0.12s', opacity: gLoading ? 0.6 : 1,
            }}
          >
            <GoogleIcon />
            {gLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontFamily: T.sans, fontSize: 12, color: 'var(--muted)', letterSpacing: 0 }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          {method === 'otp' && otpStep ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ ...S.card, padding: '12px 14px', background: 'var(--bg)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dim)', marginBottom: 3 }}>Check your inbox</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  6-digit code sent to{' '}
                  <span style={{ color: 'var(--text)', fontFamily: T.mono }}>{email}</span>
                </div>
              </div>
              <OtpInput value={otp} onChange={setOtp} />
              <button
                onClick={verifyOtp}
                disabled={otp.length < 6 || verifying}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 7,
                  padding: '12px 16px',
                  background: otp.length < 6 || verifying ? 'var(--raised)' : T.pepper,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 750,
                  color: otp.length < 6 || verifying ? 'var(--muted)' : '#ffffff',
                  cursor: otp.length < 6 || verifying ? 'not-allowed' : 'pointer',
                  transition: 'opacity 0.12s',
                }}
              >
                {verifying ? 'Verifying…' : <><LogIn size={13} /> Verify Code <ArrowRight size={13} /></>}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button onClick={reset} style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: T.mono }}>
                  ← Change email
                </button>
                <button onClick={sendOtp} style={{ background: 'none', border: 'none', fontSize: 11, color: T.info, cursor: 'pointer', fontFamily: T.mono }}>
                  Resend code
                </button>
              </div>
            </div>
          ) : method === 'magic_link' && magicSent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{
                ...S.card, padding: '14px', background: 'rgba(0,214,143,0.05)',
                borderColor: 'rgba(0,214,143,0.2)', display: 'flex', gap: 10,
              }}>
                <CheckCircle size={16} style={{ color: T.greenHex, flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Magic link sent</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
                    Click the link in your email to sign in. Works on any device.
                  </div>
                </div>
              </div>
              <button onClick={reset} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', textAlign: 'left', fontFamily: T.mono }}>
                ← Use a different email
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                {([['otp', 'OTP Code', LogIn], ['magic_link', 'Magic Link', Wand2]] as const).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={method === id}
                    className={`selectable-tab${method === id ? ' is-selected' : ''}`}
                    onClick={() => setMethod(id)}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 6, padding: '9px 0', fontSize: 13, fontWeight: 700,
                      letterSpacing: 0, textTransform: 'none',
                      fontFamily: T.sans,
                    }}
                  >
                    <Icon size={10} />
                    {label}
                  </button>
                ))}
              </div>

              <div style={{ position: 'relative' }}>
                <Mail size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }} />
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (method === 'otp' ? sendOtp() : sendMagic())}
                  autoComplete="email"
                  style={{ ...S.input, paddingLeft: 34 }}
                />
              </div>

              <button
                onClick={method === 'otp' ? sendOtp : sendMagic}
                disabled={!isValidEmail || loading}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 7,
                  padding: '12px 16px',
                  background: !isValidEmail || loading ? 'var(--raised)' : T.pepper,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 750,
                  color: !isValidEmail || loading ? 'var(--muted)' : '#ffffff',
                  cursor: !isValidEmail || loading ? 'not-allowed' : 'pointer',
                  transition: 'opacity 0.12s', fontFamily: T.mono,
                }}
              >
                {loading ? 'Sending…' : 'Continue'}
              </button>
            </div>
          )}

          {/* Trust badges from the Project Bible */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 28, marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            {[
              [<Zap color='var(--muted)' />, 'Continuous'],
              [<Globe color='var(--muted)' />, 'Cross platform'],
              [<Brain color='var(--muted)' />, 'Operational']
            ].map(([icon, label]) => (
              <div key={String(label)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 18 }}>{icon}</span>
                <span style={{ fontSize: 11, fontWeight: 650, letterSpacing: 0, color: 'var(--muted)', fontFamily: T.sans }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
